"use client"

import { useCheckPermission } from "@/hook/use-check-permission"
import {
  createVideo,
  deleteVideo,
  fetchVideos,
  getVideoById,
  getVideoDetail,
  updateVideo,
  VideoAttributes,
} from "@/services/video"
import { Pagination } from "@/types/types"
import { TablePaginationConfig } from "antd"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

export interface VideoData {
  id: number
  attributes: {
    title: string
    youtubeUrl: string
    order?: number
    createdAt: string
    updatedAt: string
    publishedAt?: string | null
    categories?: {
      data: Array<{
        id: number
        attributes: {
          name: string
          publishedAt?: string | null
        }
      }>
    }
  }
}

export const useVideo = (
  options: {
    refreshKey?: number
    listPopulate?: Record<string, unknown>
    pageSize?: number
    sortField?: string
    sortOrder?: "asc" | "desc"
  } = {},
) => {
  const { pageSize: viewPageSize = 10, sortField: viewSortField, sortOrder: viewSortOrder } = options
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  // Populate the visible columns need (Configure-the-view); ref keeps the fetch
  // callback stable while reading the latest value.
  const listPopulateRef = useRef(options.listPopulate)
  listPopulateRef.current = options.listPopulate

  const [videos, setVideos] = useState<VideoData[]>([])
  const [loading, setLoading] = useState(false)
  const [internalRefreshKey, setInternalRefreshKey] = useState(0)
  // Guards: reqSeq drops stale responses, inFlightRequestKeyRef collapses the
  // React 19 StrictMode double-invoke into one request.
  const reqSeq = useRef(0)
  const inFlightRequestKeyRef = useRef<string | null>(null)

  const { hasPermission } = useCheckPermission()

  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    pageSize: viewPageSize,
    current: 1,
    total: 0,
  })

  // pageSize is owned by "Configure view" — reset to page 1 when it (or the sort)
  // changes. No "sync page size into pagination" effect any more (that fed the
  // multi fetch — RC1); the fetch reads viewPageSize directly.
  useEffect(() => {
    setPagination(prev => (prev.page === 1 ? prev : { ...prev, page: 1 }))
  }, [viewPageSize, viewSortField, viewSortOrder])

  const updateUrl = useCallback((newParams: Record<string, any>) => {
    const current = new URLSearchParams(Array.from(searchParams.entries()))
    Object.entries(newParams).forEach(([key, value]) => {
      if (key === "page" || key === "pageSize") return

      if (value === undefined || value === null || value === "") {
        current.delete(key)
      } else {
        current.set(key, String(value))
      }
    })
    const search = current.toString()
    const query = search ? `?${search}` : ""
    router.push(`${pathname}${query}`)
  }, [pathname, router, searchParams])

  const fetchVideosData = useCallback(
    async (paramsFromUrl: Record<string, any> = {}) => {
      const { page, pageSize, title, youtubeUrl, state, ...rest } = paramsFromUrl
      const params: any = {
        page: page || String(pagination.page),
        // pageSize is owned by "Configure view" — never read back from response.
        pageSize: String(viewPageSize),
        ...rest
      }

      if (title) params.title = title
      if (youtubeUrl) params.youtubeUrl = youtubeUrl
      // Populate relations (e.g. categories) only when a visible column needs it.
      if (listPopulateRef.current && Object.keys(listPopulateRef.current).length) {
        params.populate = listPopulateRef.current
      }

      let publicationState = "preview"
      if (state === "published") {
        publicationState = "live"
      } else if (state === "draft") {
        publicationState = "preview"
        params.publishedAtNull = "true"
      }

      const requestKey = JSON.stringify({ params, publicationState })
      if (inFlightRequestKeyRef.current === requestKey) {
        return
      }
      inFlightRequestKeyRef.current = requestKey
      const seq = ++reqSeq.current

      try {
        setLoading(true)
        const response = await fetchVideos(params, publicationState)
        if (seq !== reqSeq.current) return // superseded by a newer request
        setVideos(response?.data || [])
        // Only total/current/page — pageSize stays owned by viewConfig (RC2).
        setPagination(prev => ({
          ...prev,
          total: response?.meta?.pagination?.total ?? 0,
          current: response?.meta?.pagination?.page ?? 1,
          page: response?.meta?.pagination?.page ?? 1,
        }))
      } finally {
        if (inFlightRequestKeyRef.current === requestKey) {
          inFlightRequestKeyRef.current = null
        }
        if (seq === reqSeq.current) setLoading(false)
      }
    },
    [pagination.page, viewPageSize]
  )

  useEffect(() => {
    const params: Record<string, any> = {}
    searchParams.forEach((value, key) => { params[key] = value })

    if (params.page || params.pageSize) {
      setPagination(prev => ({
        ...prev,
        page: Number(params.page) || prev.page,
        pageSize: Number(params.pageSize) || prev.pageSize,
      }))
    }

    // Apply the "Configure view" default sort when the URL specifies none.
    if (!params.sort && viewSortField) {
      params.sort = `${viewSortField}:${viewSortOrder ?? "desc"}`
    }

    fetchVideosData(params)
  }, [
    searchParams,
    fetchVideosData,
    internalRefreshKey,
    options.refreshKey,
    options.listPopulate,
    viewSortField,
    viewSortOrder,
  ])

  const onSearch = useCallback((filters: object) => {
    if (Object.keys(filters).length === 0) {
      router.replace(pathname);
      return;
    }
    setPagination(prev => ({ ...prev, page: 1 }))
    updateUrl({ ...filters, page: 1 })
  }, [updateUrl])

  const onTableChange = useCallback((paginationConfig: TablePaginationConfig, _filters?: unknown, sorter?: unknown) => {
    setPagination(prev => ({
      ...prev,
      page: paginationConfig.current || 1,
      pageSize: paginationConfig.pageSize || 10,
    }))

    const s = sorter as { field?: string | (string | number)[]; order?: "ascend" | "descend" } | undefined
    if (s && !Array.isArray(s) && s.field && s.order) {
      const field = Array.isArray(s.field) ? s.field[s.field.length - 1] : s.field
      const order = s.order === "ascend" ? "asc" : "desc"
      updateUrl({ sort: `${field}:${order}` })
    } else if (s && !Array.isArray(s) && !s.order) {
      updateUrl({ sort: undefined })
    } else {
      updateUrl({})
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [updateUrl])

  const fetchVideoById = useCallback(async (id: number) => {
    try {
      setLoading(true)
      const response = await getVideoById(id)
      return response?.data || null
    } finally {
      setLoading(false)
    }
  }, [])

  const createVideoData = useCallback(async (data: VideoAttributes) => {
    try {
      setLoading(true)
      await createVideo(data)
      return true
    } catch {
      return false
    } finally {
      setLoading(false)
    }
  }, [])

  const updateVideoData = useCallback(async (id: string, data: VideoAttributes) => {
    try {
      setLoading(true)
      await updateVideo(id, data)
      return true
    } catch {
      return false
    } finally {
      setLoading(false)
    }
  }, [])

  const deleteVideoData = useCallback(async (id: number) => {
    try {
      setLoading(true)
      await deleteVideo(id)
      setInternalRefreshKey(prev => prev + 1)
      return true
    } catch {
      return false
    } finally {
      setLoading(false)
    }
  }, [])

  const duplicateVideo = useCallback(async (id: number) => {
    try {
      setLoading(true)
      const res = await getVideoDetail(id)
      const item = res?.data
      if (!item) return false
      const payload: VideoAttributes = {
        title: `${item.attributes?.title || ""} Copy`,
        youtubeUrl: item.attributes?.youtubeUrl || "",
        order: item.attributes?.order || 99,
        publishedAt: item.attributes?.publishedAt ? new Date(item.attributes.publishedAt).toISOString() : undefined,
        categories: item.attributes?.categories ? {
          data: item.attributes.categories.data.map((cat: any) => ({ id: cat.id })),
        } : undefined,
      }
      await createVideo(payload)
      setInternalRefreshKey(prev => prev + 1)
      return true
    } catch {
      return false
    } finally {
      setLoading(false)
    }
  }, [])

  const initialFilterValues = useMemo(() => {
    const params: Record<string, any> = {}
    searchParams.forEach((value, key) => {
      if (!["page", "pageSize", "sort", "publicationState"].includes(key)) {
        params[key] = value
      }
    })
    return params
  }, [searchParams])

  return {
    videos,
    loading,
    pagination,
    initialFilterValues,
    fetchVideoById,
    createVideo: createVideoData,
    updateVideo: updateVideoData,
    deleteVideo: deleteVideoData,
    duplicateVideo,
    onSearch,
    onTableChange,
    hasPermission,
    refresh: () => setInternalRefreshKey(prev => prev + 1)
  }
}